import { createContext, useContext, type ReactElement, type ReactNode } from "react";

import { pageSecondaryChromeHeaderClassName } from "@/components/layout/appChrome";
import {
  pageNestedStackClassName,
  pageSecondaryStackClassName,
  pageStackClassName,
} from "@/components/layout/pageLayout";
import { PageHeader } from "@/components/shared/PageHeader";
import { cn } from "@/lib/utils";

/** True when rendering inside another {@link Page} (e.g. settings/project shell outlet). */
const PageNestingContext = createContext(false);

/** Whether the caller is a descendant of a {@link Page} shell (used by loading skeletons). */
export function useIsInsidePage(): boolean {
  return useContext(PageNestingContext);
}

type PageBaseProps = {
  children: ReactNode;
  className?: string;
};

type PageWithHeaderProps = PageBaseProps & {
  variant?: "default";
  title: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
  fill?: boolean;
  showHeader?: boolean;
};

type PageNestedProps = PageBaseProps & {
  variant?: "default";
  fill: false;
};

type PageSecondaryProps = PageBaseProps & {
  variant: "secondary";
  backMobile: ReactNode;
  secondaryTitle: ReactNode;
  secondaryAccessory?: ReactNode;
  fill?: false;
};

export type PageProps = PageWithHeaderProps | PageNestedProps | PageSecondaryProps;

function hasHeader(props: PageProps): props is PageWithHeaderProps {
  return "title" in props;
}

function isSecondaryPage(props: PageProps): props is PageSecondaryProps {
  return props.variant === "secondary";
}

/** Standard page shell with built-in header inside {@link PageScrollViewport}. */
export function Page(props: PageProps): ReactElement {
  const { children, className } = props;
  const isInsidePage = useContext(PageNestingContext);
  const fill = isSecondaryPage(props) ? false : (props.fill ?? true);
  const showHeader = hasHeader(props) ? (props.showHeader ?? true) : false;
  const stackClassName = isSecondaryPage(props)
    ? pageSecondaryStackClassName
    : isInsidePage || !fill
      ? pageNestedStackClassName
      : pageStackClassName;
  return (
    <PageNestingContext.Provider value={true}>
      <div
        className={cn(stackClassName, className)}
        data-slot="page"
        data-variant={isSecondaryPage(props) ? "secondary" : "default"}
      >
        {isSecondaryPage(props) ? (
          <header className={pageSecondaryChromeHeaderClassName}>
            <div className="col-start-1 flex shrink-0 items-center lg:hidden">
              {props.backMobile}
            </div>
            <div className="col-start-2 flex min-w-0 max-w-full flex-col items-center justify-center gap-1.5">
              <div className="flex min-w-0 max-w-full items-center justify-center gap-2.5">
                {props.secondaryTitle}
                {props.secondaryAccessory ? (
                  <span className="hidden shrink-0 lg:contents">{props.secondaryAccessory}</span>
                ) : null}
              </div>
              {props.secondaryAccessory ? (
                <div className="flex shrink-0 justify-center lg:hidden">
                  {props.secondaryAccessory}
                </div>
              ) : null}
            </div>
            <div aria-hidden className="col-start-3" />
          </header>
        ) : null}
        {hasHeader(props) && showHeader ? (
          <PageHeader title={props.title} actions={props.actions} back={props.back} />
        ) : null}
        {children}
      </div>
    </PageNestingContext.Provider>
  );
}
